import { createRouter, createWebHistory } from 'vue-router'
import bus from '../plugins/bus'
import store from '../store'

/**
 * Here we specify how to chunk out the application into discrete files. The overall effect is that it reduces the
 * amount of code a user has to download while using the application. On first load (the login screen) the application
 * only loads a small fraction of the code, just enough to render the login page. The code that makes of the
 * administration section of the application is also chunked out making it so that customers who might be determined
 * to reverse engineer the application will not see all aspects that make up this software. TLDR
 * Chunk for the following benefits:
 * - Better performance and load times.
 * - Intellectual Property Security by not making all application code available to users.
 */

// This is for administrators
const AdminUsers = () => import(/* webpackChunkName: "admin" */'../components/Admin/Users/Users.vue')
const NewAdmin = () => import(/* webpackChunkName: "admin" */'../components/Admin/Users/NewAdmin.vue')
const AdminCustomers = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Customers.vue')
const CreateCustomer = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/CreateCustomer.vue')
const AdminCustomer = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/EditCustomer.vue')
const CreateTableau = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/Tableau/CreateTableau.vue')
const EditTableau = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/Tableau/EditTableau.vue')
const AddTableauViews = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/Tableau/AddView.vue')
const EditTableauView = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/Tableau/EditView.vue')
const EditScheduledEmailReport = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/Tableau/EditScheduledEmailReport.vue')
const AddFilter = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/AddFilter.vue')
const AddTimeFrame = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/CPBuilder/Edit/AddTimeFrame.vue')
const EditFilter = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/EditFilter.vue')
const CreateIframe = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/SimpleIframe/Create.vue')
const EditIframe = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/SimpleIframe/Edit.vue')
const CreateReferenceFile = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/ReferenceFile/Create.vue')
const EditReferenceFile = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/ReferenceFile/Edit.vue')
const CreatePowerPoint = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/CPBuilder/Create/Create.vue')
const EditPowerPoint = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/CPBuilder/Edit/Edit.vue')
const CreateFileSharing = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/FileSharing/Create.vue')
const EditFileSharing = () => import(/* webpackChunkName: "admin" */'../components/Admin/Customer/Project/FileSharing/Edit.vue')

// This has zxcvbn. Hopefully this makes loading zxcvbn (which is HUGE) a non-blocking operation.
const ResetPassword = () => import(/* webpackChunkName: "zxcvbn" */'../components/ResetPassword/ResetPassword.vue')
const Account = () => import(/* webpackChunkName: "zxcvbn" */'../components/User/Account.vue')
const EditUser = () => import(/* webpackChunkName: "zxcvbn" */'../components/User/EditUser.vue')
const NewUser = () => import(/* webpackChunkName: "zxcvbn" */'../components/User/NewUser.vue')

// This is for logging in
const Login = () => import(/* webpackChunkName: "login" */ '../components/Login/Login.vue')
const RequestReset = () => import(/* webpackChunkName: "login" */'../components/ResetPassword/RequestReset.vue')

// This is required
const Main = () => import(/* webpackChunkName: "main" */ '../components/Main/Main.vue')
const ViewSharedReportLink = () => import(/* webpackChunkName: "main" */ '../components/SharedReportLink/ViewSharedReportLink.vue')
const SharedLinkReportExpired = () => import(/* webpackChunkName: "main" */ '../components/SharedReportLink/ReportExpired.vue')
const SharedLinkReportProblem = () => import(/* webpackChunkName: "main" */ '../components/SharedReportLink/Problem.vue')
const Tableau = () => import(/* webpackChunkName: "main" */ '../components/Projects/TableauEmbed/Visualization.vue')
const Views = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Views/Views.vue')
const ViewsGrid = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Views/ViewsGrid.vue')
const Grids = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Grids/Grids.vue')
const Projects = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Grids/Projects.vue')
const ReferenceFiles = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Grids/ReferenceFiles.vue')
const ReferenceFileView = () => import(/* webpackChunkName: "main" */ '../components/Projects/ReferenceFile/Visualization.vue')
const FileSharing = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Grids/FileSharing.vue')
const FileView = () => import(/* webpackChunkName: "main" */ '../components/Projects/FileSharing/FileView.vue')
const Users = () => import(/* webpackChunkName: "main" */ '../components/User/Users.vue')
const Dashboard = () => import(/* webpackChunkName: "admin" */ '../components/Main/Dashboard.vue')
const UserDashboard = () => import(/* webpackChunkName: "main" */ '../components/Navigation/User/UserDashboard.vue')
const Report = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Views/Report.vue')
const SharedReport = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Views/SharedReport.vue')
const ViewFeedbackReport = () => import(/* webpackChunkName: "main" */ '../components/Feedback/ViewFeedbackReport.vue')
const LoadingFeedbackReport = () => import(/* webpackChunkName: "main" */ '../components/Feedback/LoadingReport.vue')
const ViewFeedbackChat = () => import(/* webpackChunkName: "main" */ '../components/Feedback/ViewFeedbackChat.vue')
const UserFeedback = () => import(/* webpackChunkName: "main" */ '../components/Feedback/UserFeedback.vue')
const ReportExpired = () => import(/* webpackChunkName: "main" */ '../components/Feedback/ReportExpired.vue')
const FAQ = () => import(/* webpackChunkName: "main" */ '../components/Navigation/User/FAQ.vue')
const RouteWrap = () => import(/* webpackChunkName: "main" */ '../components/Navigation/RouteWrap.vue')
const SharedReportLoader = () => import(/* webpackChunkName: "main" */ '../components/Navigation/Views/SharedReportLoader.vue')
const SimpleIframe = () => import(/* webpackChunkName: "main" */ '../components/Projects/SimpleIframe/Visualization.vue')

// Secret Sauce Chunks
// PowerPoint
const PowerPoints = () => import(/* webpackChunkName: "ppt" */ '../components/Navigation/Grids/PowerPoints.vue')
const CPBuilder = () => import(/* webpackChunkName: "ppt" */ '../components/Projects/CPBuilder/CPBuilder.vue')
const PowerPointSavedReports = () => import(/* webpackChunkName: "ppt" */ '../components/Navigation/Views/CPBuilderSavedReports.vue')

// Static Pages
const Maintenance = () => import(/* webpackChunkName: "static" */ '../components/Navigation/User/Maintenance.vue')
const ExportView = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/ExportView.vue')
const SaveReport = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/SaveReport.vue')
const SendFeedback = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/SendFeedback.vue')
const RunCPBuilder = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/RunCPBuilder.vue')
const SaveCPBuilder = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/SaveCPBuilder.vue')
const InsightsSideGuide = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/InsightsSideGuide.vue')
const InsightsGlobalGuide = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/InsightsGlobalGuide.vue')
// const UnderConstruction = () => import(/* webpackChunkName: "static" */ '../components/Static/How To Guides/UnderConstruction.vue')

// iFrame pages
const SiteMaintenance = () => import(/* webpackChunkName: "iframes" */ '../components/Static/Views/SiteMaintenance.vue')

// AI pages
const ChatHistory = () => import(/* webpackChunkName: "ai" */ '../components/CustomWidgets/AIWidgets/ChatHistory.vue')
const GlobalChat = () => import(/* webpackChunkName: "ai" */ '../components/CustomWidgets/AIWidgets/GlobalChat.vue')
const UserAiDashboards = () => import(/* webpackChunkName: "ai" */ '../components/AiDash/UserAiDashboards.vue')
const AdminAiDashboards = () => import(/* webpackChunkName: "ai" */ '../components/AiDash/AdminAiDashboards.vue')
const CustomDashView = ()  => import(/* webpackChunkName: "ai" */ '../components/AiDash/CustomDashView.vue')

// Allows us to dynamically control the contents of the header tag on the document. Must be before Vue Router

// componentName does not return correctly in staging or production without hardcoding the string value, see the Vizualization route
  const routes = [
    {
      path: '/',
      redirect: '/Login'
    },
    {
      path: '/Login',
      name: 'Login',
      component: Login,
      meta: {
        componentName: Login.name,
        pageName: 'Login',
        progress: {
          func: [
            {call: 'color', modifier: 'temp', argument: '#FFFFFF'}
          ]
        }
      }
    },
    {
      path: '/ResetRequest',
      name: 'Request Password Reset',
      component: RequestReset,
      meta: {
        componentName: RequestReset.name,
        pageName: 'Request A Password Reset'
      }
    },
    {
      path: '/Reset',
      name: 'Reset',
      component: ResetPassword,
      meta: {
        componentName: ResetPassword.name,
        requiresAuth: false
      },
      // This takes the password reset token as an argument in the URL for the component to work with as a Prop
      props: (route) => ({ token: route.query.t })
    },
    {
      path: '',
      component: Main,
      meta: {
        componentName: Main.name,
        requiresAuth: true
      },
      children: [
        {
          path: '/Dashboard',
          name: 'Dashboard',
          component: Dashboard,
          meta: {
            componentName: Dashboard.name,
            pageName: 'Home'
          }
        },
        {
          path: '/Admin',
          component: RouteWrap,
          meta: {
            componentName: RouteWrap,
            requiresAdmin: true
          },
          children: [
            {
              path: 'Users',
              name: 'AdminUsers',
              component: AdminUsers,
              meta: {
                componentName: AdminUsers.name,
                pageName: 'Users'
              }
            },
            {
              path: 'User/:user_id',
              name: 'AdminEditUser',
              component: EditUser,
              meta: {
                componentName: EditUser.name,
                pageName: 'Edit User'
              }
            },
            {
              path: 'CreateAdmin',
              name: 'CreateAdmin',
              component: NewAdmin,
              meta: {
                componentName: NewAdmin.name,
                pageName: 'Create Admin'
              }
            },
            {
              path: 'Customers',
              name: 'Customers',
              component: AdminCustomers,
              meta: {
                componentName: AdminCustomers.name,
                pageName: 'Manage Customers'
              }
            },
            {
              path: 'CreateCustomer',
              name: 'CreateCustomer',
              component: CreateCustomer,
              meta: {
                componentName: CreateCustomer.name,
                pageName: 'Create Customer'
              }
            },
            {
              path: 'Customer/:customer_id',
              component: RouteWrap,
              meta: {
                componentName: RouteWrap.name
              },
              children: [
                {
                  path: '',
                  name: 'Customer',
                  component: AdminCustomer,
                  meta: {
                    componentName: AdminCustomer.name,
                    pageName: 'Edit Customer'
                  }
                },
                {
                  path: 'NewUser',
                  name: 'AdminCustomerNewUser',
                  component: NewUser,
                  meta: {
                    componentName: NewUser.name,
                    pageName: 'Add User'
                  }
                },
                {
                  path: 'User/:user_id',
                  name: 'AdminCustomerEditUser',
                  component: EditUser,
                  meta: {
                    componentName: EditUser.name,
                    pageName: 'Edit User'
                  }
                },
                {
                  path: 'NewProject',
                  component: RouteWrap,
                  meta: {
                    componentName: RouteWrap.name
                  },
                  children: [
                    {
                      path: 'Tableau',
                      name: 'CreateTableau',
                      component: CreateTableau,
                      meta: {
                        componentName: CreateTableau.name,
                        pageName: 'Create Tableau Project'
                      }
                    },
                    {
                      path: 'SimpleIframe',
                      component: CreateIframe,
                      name: 'CreateSimpleIframe',
                      meta: {
                        componentName: CreateIframe.name,
                        pageName: 'Create Simple Iframe Project'
                      }
                    },
                    {
                      path: 'PowerPoint',
                      component: CreatePowerPoint,
                      name: 'CreatePowerPoint',
                      meta: {
                        componentName: CreatePowerPoint.name,
                        pageName: 'Create Tableau Power Point Export'
                      }
                    },
                    {
                      path: 'ReferenceFile',
                      component: CreateReferenceFile,
                      name: 'CreateReferenceFile',
                      meta: {
                        componentName: CreateReferenceFile.name,
                        pageName: 'Create Reference File'
                      }
                    },
                    {
                      path: 'FileSharing',
                      component: CreateFileSharing,
                      name: 'CreateFileSharing',
                      meta: {
                        componentName: CreateFileSharing.name,
                        pageName: 'Create Repository'
                      }
                    }
                  ]
                },
                {
                  path: 'EditProject/:project_id',
                  component: RouteWrap,
                  meta: {
                    componentName: RouteWrap.name
                  },
                  children: [
                    {
                      path: 'Tableau',
                      component: RouteWrap,
                      meta: {
                        componentName: RouteWrap.name
                      },
                      children: [
                        {
                          path: '',
                          name: 'EditTableau',
                          component: EditTableau,
                          meta: {
                            componentName: EditTableau.name,
                            pageName: 'Edit Project'
                          }
                        },
                        {
                          path: 'AddViews',
                          name: 'AddTableauViews',
                          component: AddTableauViews,
                          meta: {
                            componentName: AddTableauViews.name,
                            pageName: 'Add Views To Project'
                          }
                        },
                        {
                          path: 'EditView/:view_id',
                          name: 'EditTableauView',
                          component: EditTableauView,
                          meta: {
                            componentName: EditTableauView.name,
                            pageName: 'Edit Project View'
                          }
                        },
                        {
                          path: 'AddFilter',
                          name: 'AddTableauFilter',
                          component: AddFilter,
                          meta: {
                            componentName: AddFilter.name,
                            pageName: 'Add Filter To Project'
                          }
                        },
                        {
                          path: 'Filter/:filter_id',
                          name: 'EditTableauFilter',
                          component: EditFilter,
                          meta: {
                            componentName: EditFilter.name,
                            pageName: 'Edit Project Filter'
                          }
                        },
                        {
                          path: 'EditScheduledEmailReport/:report_id',
                          name: 'EditScheduledEmailReport',
                          component: EditScheduledEmailReport,
                          meta: {
                            componentName: EditScheduledEmailReport.name,
                            pageName: 'Edit Scheduled Email Report'
                          }
                        }
                      ]
                    },
                    {
                      path: 'SimpleIframe',
                      component: RouteWrap,
                      meta: {
                        componentName: RouteWrap.name
                      },
                      children: [
                        {
                          path: 'Edit',
                          component: EditIframe,
                          name: 'EditSimpleIframe',
                          meta: {
                            componentName: EditIframe.name,
                            pageName: 'Edit Project'
                          }
                        }
                      ]
                    },
                    {
                      path: 'ReferenceFile',
                      component: RouteWrap,
                      meta: {
                        componentName: RouteWrap.name
                      },
                      children: [
                        {
                          path: 'Edit',
                          component: EditReferenceFile,
                          name: 'EditReferenceFile',
                          meta: {
                            componentName: EditReferenceFile.name,
                            pageName: 'Edit Project'
                          }
                        }
                      ]
                    },
                    {
                      path: 'FileSharing',
                      component: RouteWrap,
                      meta: {
                        componentName: RouteWrap.name
                      },
                      children: [
                        {
                          path: 'Edit',
                          component: EditFileSharing,
                          name: 'EditFileSharing',
                          meta: {
                            componentName: EditFileSharing.name,
                            pageName: 'Edit Project'
                          }
                        }
                      ]
                    },
                    {
                      path: 'TableauPowerPoint',
                      component: RouteWrap,
                      meta: {
                        componentName: RouteWrap.name
                      },
                      children: [
                        {
                          path: '',
                          name: 'EditPowerPoint',
                          component: EditPowerPoint,
                          meta: {
                            componentName: EditPowerPoint.name,
                            pageName: 'Edit PowerPoint'
                          }
                        },
                        {
                          path: 'AddViews',
                          name: 'AddPPTTableauViews',
                          component: AddTableauViews,
                          meta: {
                            componentName: AddTableauViews.name,
                            pageName: 'Add Views To Project'
                          }
                        },
                        {
                          path: 'EditView/:view_id',
                          name: 'EditPPTTableauView',
                          component: EditTableauView,
                          meta: {
                            componentName: EditTableauView.name,
                            pageName: 'Edit Project View'
                          }
                        },
                        {
                          path: 'AddFilter',
                          name: 'AddPPTTableauFilter',
                          component: AddFilter,
                          meta: {
                            componentName: AddFilter.name,
                            pageName: 'Add Filter To Project'
                          }
                        },
                        {
                          path: 'AddTimeFrame',
                          name: 'AddPPTTableauTimeFrame',
                          component: AddTimeFrame,
                          meta: {
                            componentName: AddTimeFrame.name,
                            pageName: 'Add Time Frame To Project'
                          }
                        },
                        {
                          path: 'Filter/:filter_id',
                          name: 'EditPPTTableauFilter',
                          component: EditFilter,
                          meta: {
                            componentName: EditFilter.name,
                            pageName: 'Edit Project Filter'
                          }
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: '/SharedReport/:report_id',
          component: SharedReportLoader,
          meta: {
            componentName: SharedReportLoader.name
          }
        },
        {
          path: '/Customer/:customer_id',
          component: Grids,
          meta: {
            componentName: Grids.name,
            requiresAdmin: true,
            progress: {
              func: [
                {call: 'color', modifier: 'temp', argument: '#B0BEC5'}
              ]
            }
          },
          children: [
            {
              path: 'Projects',
              alias: '',
              name: 'AdminProjectsGrid',
              component: Projects,
              meta: {
                componentName: Projects.name,
                pageName: 'Select Project'
              }
            },
            {
              path: 'Project/:project_id',
              component: Views,
              meta: {
                componentName: Views.name
              },
              children: [
                {
                  path: 'Views',
                  name: 'AdminViews',
                  component: ViewsGrid,
                  beforeEnter: (to) => {
                    if (to.params.customer_id) {
                      store.commit('navigation/setCustomerId', to.params.customer_id);
                    }
                  },
                  meta: {
                    componentName: ViewsGrid.name,
                    pageName: 'Select View'
                  }
                },
                {
                  path: 'View/:view_id/viz/t',
                  name: 'AdminTableauView',
                  component: Tableau,
                  meta: {
                    componentName: 'Visualization',
                    onViz: true
                  }
                },
                {
                  path: 'Report/:report_id/View/:view_id/viz/t',
                  component: Report,
                  children: [
                    {
                      path: '',
                      name: 'AdminTabReportView',
                      component: Tableau,
                      meta: {
                        componentName: 'Visualization',
                        onViz: true,
                        onReport: true
                      }
                    }
                  ]
                },
                {
                  path: 'SharedReport/:report_id/View/:view_id/viz/t',
                  component: SharedReport,
                  children: [
                    {
                      path: '',
                      name: 'AdminTabSharedReportView',
                      component: Tableau,
                      meta: {
                        componentName: 'Visualization',
                        onViz: true,
                        onReport: true
                      }
                    }
                  ]
                },
                {
                  path: 'sf',
                  component: SimpleIframe,
                  name: 'AdminSimpleIframe',
                  meta: {
                    componentName: SimpleIframe.name,
                    onViz: true
                  }
                }
              ]
            },
            {
              path: 'PowerPoints',
              name: 'AdminPowerPoints',
              component: PowerPoints,
              meta: {
                componentName: PowerPoints.name,
                pageName: 'Select PowerPoint'
              }
            },
            {
              path: 'PowerPoint/:project_id/SavedReports',
              name: 'AdminPowerPointSavedReports',
              component: PowerPointSavedReports,
              meta: {
                componentName: PowerPointSavedReports.name,
                pageName: 'Select Saved Report'
              }
            },
            {
              path: 'PowerPoint/:project_id',
              component: Grids,
              meta: {
                componentName: Grids.name
              },
              children: [
                {
                  path: '',
                  name: 'AdminCPBuilder',
                  component: CPBuilder,
                  meta: {
                    componentName: 'CPBuilder',
                    onViz: true
                  }
                },
                {
                  path: 'Report/:report_id/',
                  component: Report,
                  children: [
                    {
                      path: '',
                      name: 'AdminPowerPointReportView',
                      component: CPBuilder,
                      meta: {
                        componentName: 'CPBuilder',
                        onViz: true,
                        onReport: true
                      }
                    }
                  ]
                }
              ]
            },
            {
              path: 'ReferenceFiles',
              name: 'AdminReferenceFiles',
              component: ReferenceFiles,
              meta: {
                componentName: ReferenceFiles.name,
                pageName: 'Select Reference File'
              }
            },
            {
              path: 'ReferenceFile/:project_id',
              component: Grids,
              meta: {
                componentName: Grids.name
              },
              children: [
                {
                  path: '',
                  component: ReferenceFileView,
                  name: 'AdminReferenceFile',
                  meta: {
                    componentName: ReferenceFileView.name,
                    onViz: true,
                  }
                }
              ]
            },
            {
              path: 'FileSharing',
              name: 'AdminFileSharing',
              component: FileSharing,
              meta: {
                componentName: FileSharing.name,
                pageName: 'Select Repository'
              }
            },
            {
              path: 'FileSharing/:project_id',
              component: Grids,
              meta: {
                componentName: Grids.name
              },
              children: [
                {
                  path: '',
                  component: FileView,
                  name: 'AdminFileView',
                  meta: {
                    componentName: FileView.name,
                    pageName: 'Repository'
                  }
                }
              ]
            }
          ]
        },
        {
          path: '/UserFeedback',
          component: UserFeedback,
          name: 'UserFeedback',
          meta: {
            componentName: UserFeedback.name,
            pageName: 'Your Feedback Status',
            props: true 
          }
        },
        {
          path: '/faq',
          component: FAQ,
          name: 'FAQ',
          meta: {
            componentName: FAQ.name,
            pageName: 'FAQ'
          }
        },
        {
          path: '/how-to/export-view',
          name: 'Export View',
          component: ExportView,
          meta: {
            componentName: ExportView.name,
            pageName: 'FAQ: Export View'
          }
        },
        {
          path: '/how-to/save-report',
          name: 'Save Report',
          component: SaveReport,
          meta: {
            componentName: SaveReport.name,
            pageName: 'FAQ: Save Report'
          }
        },
        {
          path: '/how-to/send-feedback',
          name: 'Send Feedback',
          component: SendFeedback,
          meta: {
            componentName: SendFeedback.name,
            pageName: 'FAQ: Send Feedback'
          }
        },
        {
          path: '/how-to/run-cp-builder',
          name: 'Run CP Builder',
          component: RunCPBuilder,
          meta: {
            componentName: RunCPBuilder.name,
            pageName: 'FAQ: Run CP Builder'
          }
        },
        {
          path: '/how-to/save-cp-builder',
          name: 'Save CP Builder Report',
          component: SaveCPBuilder,
          meta: {
            componentName: SaveCPBuilder.name,
            pageName: 'FAQ: Save CP Builder Report'
          }
        },
        {
          path: '/how-to/insights-side-guide',
          name: 'InsightsSideGuide',
          component: InsightsSideGuide,
          meta: {
            componentName: InsightsSideGuide.name,
            pageName: 'Insights Side Guide'
          }
        },
        {
          path: '/how-to/insights-global-guide',
          name: 'InsightsGlobalGuide',
          component: InsightsGlobalGuide,
          meta: {
            componentName: InsightsGlobalGuide.name,
            pageName: 'Insights Global Guide'
          }
        },
        {
          path: '/ViewFeedbackReport/:feedback_report_id',
          component: ViewFeedbackReport,
          meta: {
            componentName: ViewFeedbackReport.name,
            requiresAdmin: true,
            onReport: true
          },
          children: [
            {
              path: '',
              name: 'LoadingFeedbackReport',
              component: LoadingFeedbackReport,
              meta: {
                componentName: LoadingFeedbackReport.name
              }
            },
            {
              path: 'gone',
              component: ReportExpired,
              name: 'ReportExpired',
              meta: {
                componentName: ReportExpired.name
              }
            },
            {
              path: 'customer/:customer_id/project/:project_id/view/:view_id/viz/t',
              name: 'AdminTabFeedbackReportView',
              component: Tableau,
              meta: {
                componentName: 'Visualization',
                onViz: true
              }
            },
            {
              path: 'customer/:customer_id/PowerPoint/:project_id/',
              name: 'AdminPowerPointFeedbackReportView',
              component: CPBuilder,
              meta: {
                componentName: 'CPBuilder',
                onViz: true,
                onReport: true
              }
            }
          ]
        },
        {
          path: '/SharedReportLink/:link_id',
          component: ViewSharedReportLink,
          meta: {
            componentName: ViewSharedReportLink.name,
            onReport: true
          },
          children: [
            {
              path: 'gone',
              component: SharedLinkReportExpired,
              name: 'SharedLinkExpired',
              meta: {
                componentName: SharedLinkReportExpired.name
              }
            },
            {
              path: 'problem',
              component: SharedLinkReportProblem,
              name: 'SharedReportLinkProblem',
              meta: {
                componentName: SharedLinkReportProblem.name
              }
            },
            {
              path: 'customer/:customer_id/project/:project_id/view/:view_id/viz/t',
              name: 'AdminTabSharedLinkReportView',
              component: Tableau,
              meta: {
                componentName: 'Visualization',
                onViz: true,
                requiresAdmin: true
              }
            },
            {
              path: 'project/:project_id/view/:view_id/viz/t',
              name: 'TabSharedLinkReportView',
              component: Tableau,
              meta: {
                componentName: 'Visualization',
                onViz: true,
                requiresUser: true
              }
            }
          ]
        },
        {
          path: '/ViewFeedbackChat/:chat_id',
          component: ViewFeedbackChat,
          meta: {
            componentName: ViewFeedbackChat.name,
            requiresAdmin: true,
          }
        },
        {
          path: '/ReferenceFiles',
          component: Grids,
          meta: {
            componentName: Grids.name,
            requiresUser: true
          },
          children: [
            {
              path: '',
              name: 'ReferenceFiles',
              component: ReferenceFiles,
              meta: {
                componentName: ReferenceFiles.name,
                pageName: 'Select Reference File'
              }
            },
            {
              path: 'Project/:project_id',
              component: ReferenceFileView,
              name: 'ReferenceFile',
              meta: {
                componentName: ReferenceFileView.name,
                onViz: true,
              }
            }
          ]
        },
        {
          path: '/FileSharing',
          component: Grids,
          meta: {
            componentName: Grids.name,
            requiresUser: true
          },
          children: [
            {
              path: '',
              name: 'FileSharing',
              component: FileSharing,
              meta: {
                componentName: FileSharing.name,
                pageName: 'Select Repository'
              }
            },
            {
              path: 'Project/:project_id',
              component: FileView,
              name: 'FileView',
              meta: {
                componentName: FileView.name,
                pageName: 'Repository'
              }
            }
          ]
        },
        {
          path: '/PowerPoints',
          component: Grids,
          meta: {
            componentName: Grids.name,
            requiresUser: true
          },
          children: [
            {
              path: '',
              name: 'PowerPoints',
              component: PowerPoints,
              meta: {
                componentName: PowerPoints.name,
                pageName: 'Select PowerPoint'
              }
            },
            {
              path: 'Project/:project_id',
              name: 'CPBuilder',
              component: CPBuilder,
              meta: {
                componentName: 'CPBuilder',
                onViz: true
              }
            },
            {
              path: 'SavedReports/:project_id',
              name: 'PowerPointSavedReports',
              component: PowerPointSavedReports,
              meta: {
                componentName: PowerPointSavedReports.name,
                pageName: 'Select Saved Report'
              }
            },
            {
              path: 'Project/:project_id/Report/:report_id/',
              component: Report,
              children: [
                {
                  path: '',
                  name: 'PowerPointReportView',
                  component: CPBuilder,
                  meta: {
                    componentName: 'CPBuilder',
                    onViz: true,
                    onReport: true
                  }
                }
              ]
            }
          ]
        },
        {
          path: '/Projects',
          component: Grids,
          meta: {
            componentName: Grids.name,
            requiresUser: true
          },
          children: [
            {
              path: '',
              name: 'ProjectsGrid',
              component: Projects,
              meta: {
                componentName: Projects.name,
                pageName: 'Select Project'
              }
            },
            {
              path: '/Project/:project_id',
              component: Grids,
              meta: {
                componentName: Grids.name
              },
              children: [
                {
                  path: '',
                  component: Views,
                  meta: {
                    componentName: Views.name
                  },
                  children: [
                    {
                      path: 'Views',
                      name: 'ViewsGrid',
                      component: ViewsGrid,
                      beforeEnter: (to) => {
                        if (to.params.customer_id) {
                          store.commit('navigation/setCustomerId', to.params.customer_id);
                        }
                      },
                      meta: {
                        componentName: ViewsGrid.name,
                        pageName: 'Select View'
                      }
                    },
                    {
                      path: 'View/:view_id/viz/t',
                      name: 'TableauView',
                      component: Tableau,
                      meta: {
                        componentName: 'Visualization',
                        onViz: true
                      }
                    },
                    {
                      path: 'Report/:report_id/View/:view_id/viz/t',
                      component: Report,
                      children: [
                        {
                          path: '',
                          name: 'TabReportView',
                          component: Tableau,
                          meta: {
                            componentName: 'Visualization',
                            pageName: '',
                            onViz: true,
                            onReport: true
                          }
                        }
                      ]
                    },
                    {
                      path: 'SharedReport/:report_id/View/:view_id/viz/t',
                      component: SharedReport,
                      children: [
                        {
                          path: '',
                          name: 'TabSharedReportView',
                          component: Tableau,
                          meta: {
                            componentName: 'Visualization',
                            pageName: '',
                            onViz: true,
                            onReport: true
                          }
                        }
                      ]
                    }
                  ]
                },
                {
                  path: 'sf',
                  component: SimpleIframe,
                  name: 'SimpleIframe',
                  meta: {
                    componentName: SimpleIframe.name
                  }
                }
              ]
            }
          ]
        },
        {
          path: '/Users',
          component: RouteWrap,
          meta: {
            componentName: RouteWrap.name,
            requiresUser: true,
            requiresAbility: ['viewUsers']
          },
          children: [
            {
              path: '',
              component: Users,
              name: 'CustomerUsers',
              meta: {
                componentName: Users.name,
                pageName: 'Users'
              }
            },
            {
              path: 'NewUser',
              name: 'NewUser',
              component: NewUser,
              meta: {
                componentName: NewUser.name,
                pageName: 'Create New User',
                requiresAbility: ['addUsers']
              }
            },
            {
              // This needs to be the last object in this Array.
              path: ':user_id',
              name: 'EditUser',
              component: EditUser,
              meta: {
                componentName: EditUser.name,
                pageName: 'Edit User'
              }
            }
          ]
        },
        {
          path: '/Account',
          name: 'AccountSettings',
          component: Account,
          meta: {
            componentName: Account.name,
            pageName: 'Account'
          }
        },
        {
          path: '/Maintenance',
          name: 'Maintenance',
          component: Maintenance,
          meta: {
            componentName: Maintenance.name,
            pageName: 'Maintenance'
          }
        },
        {
          path: '/ChatHistory',
          name: 'ChatHistory',
          component: ChatHistory,
          props: { chatMode: 'global' },
          meta: {
            componentName: ChatHistory.name,
            pageName: 'CP Insights™ new chat'
          }
        },
        {
          path: '/ChatHistory/:chat_id',
          name: 'ChatHistory1',
          component: ChatHistory,
          props: route => ({ chatMode: 'global', chat_id: route.params.chat_id }),
          meta: {
            componentName: ChatHistory.name,
            pageName: 'CP Insights™'
          }
        },
        {
          path: '/CustomDashboards',
          name: 'CustomDashboards',
          component: UserAiDashboards,
          meta: {
            componentName: UserAiDashboards.name,
            pageName: 'Insights™ Sandbox'
          }
        },
        {
          path: '/CustomDashboards/:customer_id',
          name: 'AdminCustomDashboards',
          component: AdminAiDashboards,
          meta: {
            componentName: AdminAiDashboards.name,
            pageName: 'Customer Insights™ Sandbox'
          }
        },
        {
          path: '/CustomDashboard/:dashboard_id',
          name: 'DashboardPage',
          component: CustomDashView,
          meta: {
            componentName: CustomDashView.name,
            pageName: 'Insights™ Sandbox'
          }
        },
        {
          path: '/DashboardHistory',
          name: 'DashboardHistory',
          component: ChatHistory,
          props: { chatMode: 'dashboard' },
          meta: {
            componentName: ChatHistory.name,
            pageName: 'Sandbox AI Chat'
          }
        },
        {
          path: '/DashboardHistory/:chat_id',
          name: 'DashboardHistoryWithId',
          component: ChatHistory,
          props: route => ({ chatMode: 'dashboard', chat_id: route.params.chat_id }),
          meta: {
            componentName: ChatHistory.name,
            pageName: 'Sandbox AI Chat'
          }
        },
        {
          path: 'InsightsGlobal',
          name: 'New Global Chat',
          component: GlobalChat,
          meta: {
            componentName: GlobalChat.name,
            pageName: 'CP Insights'
          }
        },
        {
          path: 'InsightsGlobal/:chat_id',
          name: 'Global Chat',
          component: GlobalChat,
          meta: {
            componentName: GlobalChat.name,
            pageName: 'CP Insights'
          }
        },
      ]
    },
    /***************************************************
     * The Following routes are specific to USHBC stuff
     ***************************************************/
    {
      path: '/site-maintenance',
      name: 'SiteMaintenance',
      component: SiteMaintenance,
      meta: {
        componentName: SiteMaintenance.name,
        pageName: 'Site Maintenance'
      }
    },
  ]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.onError(function (err) {
  console.error(err)
  bus.emit('notification', err, {
    color: 'error',
    dark: true
  })
})

function goHome (next) {
  if (store.getters.role === 'admin') {
    next({name: 'Dashboard', replace: true})
  } else {
    next({name: 'ProjectsGrid', replace: true})
  }
}

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (store.state.authenticated) {
      bus.emit('notification-close');
      // Make sure that the user doesn't need to reset their password.
      if (!store.state.user.resetFlag || (store.state.user.resetFlag && store.getters.isPoser)) {
        if (to.matched.some(record => record.meta.requiresAdmin)) {
          if (store.getters.role === 'admin') {
            next();
          } else {
            bus.emit('notification', `You can't go to that page.`, {
              color: 'error',
              dark: true
            });
            next({ name: 'Dashboard', replace: true });
          }
        } else if (to.matched.some(record => record.meta.requiresUser)) {
          if (store.getters.role === 'user') {
            try {
              // Use await to handle the async action
              const inMaintenanceMode = await store.dispatch('user/checkMaintenanceMode');
              if (inMaintenanceMode) {
                return next({ name: 'Maintenance', replace: true });
              }
              if (to.matched.some(record => record.meta.requiresAbility)) {
                let result = [...new Set(
                  to.matched
                    .filter(o => o.meta && o.meta.hasOwnProperty('requiresAbility'))
                    .map(o => o.meta.requiresAbility)
                    .flat()
                )];
                if (store.getters['isAllowed'](result)) {
                  next();
                } else {
                  // Await the abilities check
                  await store.dispatch('user/getUserAbilities');
                  if (store.getters['isAllowed'](result)) {
                    next();
                  } else {
                    bus.emit('notification', 'You don\'t have permission to access that page.', { color: 'error', dark: true });
                    next({ name: 'ProjectsGrid', replace: true });
                  }
                }
              } else {
                next();
              }
            } catch (err) {
              bus.emit('notification', 'You don\'t have permission to access that page.', { color: 'error', dark: true });
              next({ name: 'ProjectsGrid', replace: true });
            }
          } else {
            goHome(next);
            bus.emit('notification', 'Access Denied', { color: 'error', dark: true });
          }
        } else {
          next();
        }
      } else {
        // Have the user reset their password.
        next({ name: 'Reset' });
      }
    } else {
      store.commit('setInitialPath', to.path);
      // If the user has refreshed while they were on a page, we don't want them to lose their browsing history.
      next({ name: 'Login', replace: true });
    }
  } else {
    next();
  }
});

export default router
